import { useContext, useMemo } from 'react'
import { TOrderMapKey } from '@app.feature/activityFilter/store/slice/orderSlice'
import useActivityFilterStore from '@app.feature/activityFilter/store/storeActivityFilter'
import useJobCategoryStore from '@app.feature/jobCategory/store/store.jobCategory'
import { FilterByKeys, RecruitListScreenContext } from '../../context'
import { FilterListItem } from './RecruitFilterChips.mobile'

export type TFilterChipItem = {
  id: string
  name: string
}

export type TFilterChipItemWithInfo = TFilterChipItem & {
  fieldKey: FilterByKeys
  order?: number
}

export const useRecruitFilterChips = (filterList: FilterListItem[]) => {
  const { state, dispatch } = useContext(RecruitListScreenContext)

  const showSelectedIds = useJobCategoryStore((state) => state.showSelectedIds)
  const getCategoriesByIds = useJobCategoryStore(
    (state) => state.getCategoriesByIds,
  )
  const resetState = useJobCategoryStore((state) => state.resetState)
  const getChainingLabelById = useJobCategoryStore(
    (state) => state.getChainingLabelById,
  )
  const getOrderByKey = useActivityFilterStore((state) => state.getOrderByKey)

  const selectedCategories = getCategoriesByIds(showSelectedIds)

  const handleReset = () => {
    dispatch({
      type: 'FILTER_BY_INIT',
    })
    resetState()
  }

  const handleKeywordDelete = () => {
    dispatch({
      type: 'FILTER_BY_SET_KEYWORD',
      listItem: '',
      fieldName: 'q',
    })
  }

  const handleChipRemove = (filterItem: TFilterChipItemWithInfo) => {
    dispatch({
      type: 'FILTER_BY_REMOVE_FILTER_ITEM',
      listItem: {
        __typename: 'ActivityFilterItemByID',
        id: filterItem.id,
        name: filterItem.name,
      },
      fieldName: filterItem.fieldKey,
    })
  }

  const handleChipRemoveMobile = (filterItem: TFilterChipItemWithInfo) => {
    if (filterItem.fieldKey === 'q') {
      return handleKeywordDelete()
    }
    handleChipRemove(filterItem)
  }

  const filterByKeys = Object.keys(state.filterBy) as FilterByKeys[]
  const selectedFilterItems = useMemo(() => {
    const result: TFilterChipItemWithInfo[] = []

    for (const key of filterByKeys) {
      const targetFilter = state.filterBy[key]
      // 기업명의 경우
      if (key === 'q' && targetFilter) {
        result.push({
          fieldKey: 'q',
          id: 'keyword',
          name: targetFilter as string,
          order: 999,
        })
        continue
      }
      // 직무의 경우
      if (key === 'categoryIDs' && Array.isArray(targetFilter)) {
        targetFilter.map((categoryId) => {
          const order = getOrderByKey(`categoryIDs.${categoryId}`)
          return result.push({
            fieldKey: 'categoryIDs',
            id: categoryId,
            name: getChainingLabelById(categoryId),
            order,
          })
        })
        continue
      }
      const targetFilterData = filterList.find(
        (listItem) => listItem.filterKey === key,
      )
      if (!targetFilter || !targetFilterData || !Array.isArray(targetFilter)) {
        continue
      }
      targetFilter.map((filterId) => {
        const targetFilterItem = targetFilterData.filterItems.find(
          (item) => item.id === filterId,
        )

        if (targetFilterItem) {
          const order = getOrderByKey(
            `${key as TOrderMapKey}.${targetFilterItem.id}`,
          )
          result.push({
            fieldKey: key,
            id: filterId,
            name: targetFilterItem?.name || '',
            order,
          })
        }
        return null
      })
    }

    return result
  }, [state.filterBy])

  const selectedFilterPcItems = useMemo(() => {
    return selectedFilterItems.filter(
      (item) => item.fieldKey !== 'categoryIDs' && item.fieldKey !== 'q',
    )
  }, [selectedFilterItems])

  const selectedFilterMobileItems = useMemo(() => {
    return selectedFilterItems.map((item) => {
      if (item.fieldKey !== 'categoryIDs') {
        return item
      }
      return {
        ...item,
        name: getChainingLabelById(item.id),
      }
    })
  }, [selectedFilterItems])

  const filterIsEmpty =
    filterByKeys.reduce((acc, cur) => {
      const targetFilter = state.filterBy[cur]

      if (Array.isArray(targetFilter)) {
        return acc + targetFilter?.length || 0
      }

      return targetFilter ? acc + 1 : acc
    }, 0) === 0

  const keyword = state.filterBy.q

  return {
    selectedCategories,
    selectedFilterPcItems,
    selectedFilterMobileItems,
    filterIsEmpty,
    keyword,
    onReset: handleReset,
    onKeywordDelete: handleKeywordDelete,
    onChipRemove: handleChipRemove,
    onChipRemoveMobile: handleChipRemoveMobile,
  }
}
